import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';
import {
  countSelectedLeafAreas,
  getLeafAreas,
  useGetGeoAreaTree
} from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { useConfirmationDialog } from '@/features/popup/components/ConfirmationDialog';
import { OrganizationPostConfirmationContent } from '@/features/post-editor/components/OrganizationPostConfirmationContent';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type Props = {
  organizationId?: string;
  depth?: number;
  form: UseFormReturn<PostFormValues>;
  onConfirm: () => void;
};

export const useOrganizationPostConfirmation = ({
  organizationId,
  depth,
  form,
  onConfirm
}: Props) => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const showConfirmation = useConfirmationDialog();

  const { data: ownOrganizations } = useGetOwnOrganizations();
  const { data: geoAreaTree } = useGetGeoAreaTree(organizationId, depth);

  const selectedOrganizationName = organizationId
    ? ownOrganizations?.find((org) => org.id === organizationId)?.name
    : undefined;

  const confirmAndSubmit = useCallback(() => {
    if (!organizationId) {
      onConfirm();
      return;
    }

    const selectedIds = form.getValues('geoAreaIds') ?? [];
    const selectedCount = countSelectedLeafAreas(geoAreaTree, selectedIds);
    const totalCount = getLeafAreas(geoAreaTree).length;

    showConfirmation({
      labels: t('alerts.confirmOrganizationPost', { returnObjects: true }),
      content: (
        <OrganizationPostConfirmationContent
          senderLabel={t('alerts.confirmOrganizationPost.sender')}
          senderName={selectedOrganizationName ?? ''}
          visibleInLabel={t('alerts.confirmOrganizationPost.visibleIn')}
          visibleCountLabel={t('alerts.confirmOrganizationPost.visibleCount', {
            selected: selectedCount,
            total: totalCount
          })}
        />
      ),
      onConfirm
    });
  }, [
    organizationId,
    form,
    geoAreaTree,
    selectedOrganizationName,
    showConfirmation,
    t,
    onConfirm
  ]);

  return { confirmAndSubmit };
};
